import { WarningTwoTone } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { Modal } from 'antd';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { RequestStatus } from '../../graphql/generated/graphql.ts';
import { useAppStore } from '../../hooks/store/useAppStore';
import { useSubscriptions } from '../../socket/useSubscription.ts';
import { STALE_REQUEST_STATUS_QUERY } from './StaleRequestGuard.gql';

const EDITABLE_STATUSES = new Set([RequestStatus.NEW, RequestStatus.PRINTED]);

export const StaleRequestGuard: React.FC<{ saving?: boolean }> = ({ saving = false }) => {
  const requestIdForUpdate = useAppStore(state => state.requestIdForUpdate);
  const [isStale, setIsStale] = useState(false);
  const navigate = useNavigate();

  const skip = !requestIdForUpdate || saving;

  const { data } = useQuery(STALE_REQUEST_STATUS_QUERY, {
    variables: { requestId: requestIdForUpdate },
    skip,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.request && !EDITABLE_STATUSES.has(data.request.status)) {
      setIsStale(true);
    }
  }, [data]);

  const onRequestChanged = useCallback(() => {
    setIsStale(true);
  }, []);

  useSubscriptions(
    [
      { type: 'request-sent', id: requestIdForUpdate, skip },
      { type: 'request-updated', id: requestIdForUpdate, skip },
    ],
    onRequestChanged
  );

  const handleOk = useCallback(() => {
    navigate('/offene-anforderungen');
  }, [navigate]);

  return (
    <Modal
      title={
        <>
          <WarningTwoTone twoToneColor="#faad14" /> Anforderung nicht mehr bearbeitbar
        </>
      }
      open={isStale}
      closable={false}
      keyboard={false}
      maskClosable={false}
      cancelButtonProps={{ hidden: true }}
      okText="OK"
      onOk={handleOk}
    >
      <p>
        Die Anforderung wurde in der Zwischenzeit von einem anderen Benutzer oder in einem anderen Fenster modifiziert und kann daher nicht
        mehr bearbeitet werden.
      </p>
      <p>Ihre aktuellen Änderungen können leider nicht übernommen werden.</p>
    </Modal>
  );
};
